import React from 'react';
import { useFormikContext } from 'formik';
import { TextInput, Checkbox } from '@dashboard/components/Formik';
import Select, { SelectField } from '@dashboard/components/Formik/components/Select';
import { Field } from '@dashboard/components/Formik/Form';
import { currencyList } from '@dashboard/services/currency';
import { renderFields, getIsFieldHidden } from '@dashboard/components/Formik/formUtils';
import { Label } from '@dashboard/components/Form';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import { usePriceLockState } from './usePriceLockState';
import PricePlanSelector from './PricePlanSelector';
import { countEnabledPrices, shouldBlockZeroPrice } from './priceHelpers';
import { cn } from '@dashboard/lib/utils';

const currencyOptions = Object.keys(currencyList).map(key => ({
  value: currencyList[key].code,
  label: currencyList[key].code.toUpperCase(),
}));

// Friendly "Repeats" presets mapping to interval + intervalCount, so merchants
// pick "Monthly" rather than translating "every 1 month(s)". Anything that
// doesn't match a preset falls back to a Custom mode with the raw inputs.
const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily', interval: 'day', intervalCount: 1 },
  { value: 'weekly', label: 'Weekly', interval: 'week', intervalCount: 1 },
  { value: 'monthly', label: 'Monthly', interval: 'month', intervalCount: 1 },
  { value: 'quarterly', label: 'Every 3 months', interval: 'month', intervalCount: 3 },
  { value: 'semiannual', label: 'Every 6 months', interval: 'month', intervalCount: 6 },
  { value: 'yearly', label: 'Yearly', interval: 'year', intervalCount: 1 },
];

const months = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const daysOfMonth = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * Live "Price must be more than 0" error for a fixed 0/blank price that can't
 * join a 2+-price product. Its own Formik subscription re-renders it on every
 * value change — the amount block it sits in is memoized and won't re-run its
 * renderField closure, so a value read there would go stale (same reason
 * FormError works from inside the block).
 */
function AmountBlockedError({ index, isNew }) {
  const { values, initialValues } = useFormikContext();
  const blocked = shouldBlockZeroPrice(values?.prices?.[index], {
    prices: values?.prices,
    draftId: values?.__draftPriceId,
    isNew,
    initialValues,
  });
  if (!blocked) return null;
  return <FormErrorBase>Price must be more than 0</FormErrorBase>;
}

// Helper to get nested Formik value by dot/bracket path.
function getNestedValue(obj, path) {
  if (!path) return obj;
  return path
    .split(/[.[\]]+/)
    .filter(Boolean)
    .reduce((acc, key) => acc && acc[key], obj);
}

/**
 * Form fields for a single Price subdoc, laid out per the axis-v2 design.
 * fieldPrefix is the Formik field path, e.g. "prices[0]".
 * currencyLocked disables the currency input (non-default prices inherit it);
 *   currencyTooltip explains why, on the label, when a caller locks it for a
 *   reason the merchant would not otherwise guess.
 * lockBilling — true when the price already exists (has _id), which hides the
 *   Billing/Pricing/Schedule radios (false shows them).
 * isNew — true in the page-create flow. Label/Description/Badge are hidden only
 *   for a brand-new price draft there (isNew && !lockBilling); a committed/edited
 *   price always shows them. When shown, Label and Description are disabled until
 *   there are 2+ active prices, while Badge stays editable.
 * singlePrice — true for the lone price on a single-price product's edit view.
 *   Label, Description and the "Additional settings" disclosure are hidden there
 *   (only Badge, the price, and the discount/setup-fee rows remain); a multi-price
 *   product keeps all of them.
 * currencyAsSuffix — drops the Currency select and prints the code inside the
 *   amount field instead. For a caller where the currency is not a choice at all
 *   (an order bump inherits the primary's), a disabled dropdown reads as a
 *   setting the seller has failed to unlock; the suffix just states the fact.
 *   `currencyLocked` stays the right control where the currency WAS chosen and
 *   is now frozen — a purchased price — because the dropdown still shows a
 *   decision that was made.
 * hideBadge — drops the Badge field so the caller can render it itself. The
 *   order-bump form does: Highlight belongs with the card's other copy there,
 *   below the image, rather than at the tail of the price block.
 * hideLabel — drops the Label a single PWYW price shows as its checkout
 *   heading. An order bump card has no such heading.
 * allowSetupFee — false drops every setup-fee affordance (the reveal row, its
 *   body, and the paid-trial option, which is a setup fee by another name).
 *   Order bump products need this: the server 400s a bump carrying a setup fee,
 *   so the form must not offer a shape that cannot be saved.
 * allowRecurring / allowPaymentPlan / allowPayWhatYouWant — same idea for the
 *   three choices a new price otherwise offers. A radio left with one option is
 *   dropped rather than shown, since there is nothing to choose. Order bumps
 *   turn off payment plans outright (server-rejected), and recurring only
 *   where the page product has a recurring price for the bump to ride.
 * allowRecurringPayWhatYouWant — false keeps PWYW to one-time prices: the
 *   Pricing radio is not offered on a recurring price, and switching to
 *   recurring resets it to fixed. Order bumps need this (server-rejected).
 */
export default function PriceForm({
  fieldPrefix,
  currencyLocked = false,
  currencyAsSuffix = false,
  price,
  lockBilling = false,
  isNew = false,
  singlePrice = false,
  hideBadge = false,
  hideLabel = false,
  allowSetupFee = true,
  // Explains a currency the caller has locked. Order bumps need it: the lock is
  // not about Stripe immutability there but about the placement — a mismatched
  // currency makes resolveOrderBumpOffers drop the bump from every page.
  currencyTooltip,
  allowRecurring = true,
  allowPaymentPlan = true,
  allowPayWhatYouWant = true,
  allowRecurringPayWhatYouWant = true,
  // TODO(pricing-type-revival): variants-priced mode hides the base amount.
  hideAmount = false,
}) {
  const { values, setFieldValue, setFieldError, errors, touched } = useFormikContext();
  const priceValues = getNestedValue(values, fieldPrefix) || {};
  const { billingTypeLocked } = usePriceLockState(price);
  // A disabled price (not shown to customers) dims its whole form and blocks
  // interaction, mirroring how a disabled variant renders. The re-enable control
  // (the kebab menu) lives in PriceCard, outside this form, so it stays usable.
  const isPriceDisabled = price?.enabled === false;

  const index = (() => {
    const m = /\[(\d+)\]|\.(\d+)(?:\.|$)/.exec(fieldPrefix || '');
    return m ? Number(m[1] ?? m[2]) : 0;
  })();
  // Config-driven fields use dot-notation names (prices.0.amount) so the engine's
  // value-tracking memo (which splits paths on ".") sees per-field value changes.
  const prefix = (fieldPrefix || '').replace(/\[(\d+)\]/g, '.$1');

  function field(name) {
    return prefix ? `${prefix}.${name}` : name;
  }

  const pv = formValues => formValues?.prices?.[index] || {};

  // Config-field onChange closures can go stale: the engine memoizes a named
  // field by its OWN value (fieldPropsAreEqual), so e.g. the Billing-anchor
  // checkbox does NOT re-render when `interval` changes — its handler keeps
  // the priceValues captured at its last render and misses a later switch to
  // yearly (the "accepted the January default but the yearly anchor still
  // rejected" bug: the seed never fired, month stayed '' and the native
  // select ghosted January). Handlers read through this ref so they always
  // see call-time values regardless of the memo.
  const valuesRef = React.useRef(values);
  valuesRef.current = values;
  const freshPrice = () => pv(valuesRef.current);

  // The "Price must be more than 0" block error is set manually (there's no
  // validateOnChange). Formik won't clear it on its own, so clear it once the
  // amount becomes valid (> 0). Read errors through a ref: the amount block is a
  // memoized field, so its onChange closure can hold stale `errors`.
  const errorsRef = React.useRef(errors);
  errorsRef.current = errors;
  const clearAmountErrorIfValid = e => {
    const v = Number(String(e?.target?.value ?? '').trim());
    if (v > 0 && getNestedValue(errorsRef.current, field('amount'))) {
      setFieldError(field('amount'), undefined);
    }
  };

  // Currency is shared by the whole product, but a change is DEFERRED: editing it only
  // updates THIS price's own currency input. The product currency, the other prices, and
  // the "add another price" draft don't change until you Save — that's when
  // reconcileCurrencyOnSave propagates it to everything (with a "Change currency of all
  // prices?" confirm on a multi-price product). The exception is a single (or first)
  // price: there's nothing else to disrupt, so it applies to the product immediately —
  // which also keeps a newly-added second price in sync from the start.
  // (Disabled once a price has been purchased — see `currencyLocked` — Stripe immutability.)
  function onCurrencyChange(newCurrency) {
    if (!newCurrency || newCurrency === freshPrice().currency) return;
    setFieldValue(field('currency'), newCurrency);
    if ((valuesRef.current.prices || []).length <= 1) {
      setFieldValue('currency', newCurrency);
    }
  }

  const billingType = priceValues.billingType || 'one_time';
  // Label/Description are disabled (not hidden) until a SECOND price exists — they
  // matter once buyers choose between prices. Count by price existence (not priced
  // amount) so a freshly-added empty second price enables them immediately.
  //
  // The page-create flow always keeps a trailing empty new-price draft; it isn't a
  // real price the buyer will see, so exclude it from this count — otherwise a lone
  // committed price looks like two and wrongly enables Label/Description. The edit
  // flow only has a draft after an explicit "Add another price", so it counts there
  // (a genuine second price being added).
  const draftId = values.__draftPriceId ?? null;
  const enabledPriceCount = countEnabledPrices(
    isNew ? (values.prices || []).filter(p => (p._id || p.key) !== draftId) : values.prices,
  );
  // Identity fields (label/description/badge) are hidden only for a brand-new
  // price draft in the page-create flow; an edited/committed price shows them.
  const hideIdentity = isNew && !lockBilling;
  const isRecurring = billingType === 'recurring';
  const payWhatYouWant = !!priceValues.payWhatYouWant;
  const managedInStripe = !!priceValues.managedInStripe;
  // Current trial mode, as a function of arbitrary form values so the config-field
  // `visible` predicates re-evaluate correctly through the engine memo.
  const trialOf = pVals => pVals.__trial || (pVals.trialPeriodDays > 0 ? 'free' : 'none');
  const currencyCode = (priceValues.currency || 'usd').toUpperCase();

  // RadioCard.Group writes the bound field itself; these onChange callbacks keep
  // the underlying price fields in sync with the chosen UI option.
  const onBillingChange = next => {
    const p = freshPrice();
    if (next === 'recurring') {
      if (!p.interval) setFieldValue(field('interval'), 'month');
      if (!p.intervalCount) setFieldValue(field('intervalCount'), 1);
      // Default the recurring schedule to Subscription so a card is selected.
      if (!p.__schedule) setFieldValue(field('__schedule'), 'subscription');
      if (!allowRecurringPayWhatYouWant) {
        setFieldValue(field('payWhatYouWant'), false);
        setFieldValue(field('__pricing'), 'fixed');
      }
    } else {
      // one-time can't be a payment plan
      setFieldValue(field('planIterations'), '');
      setFieldValue(field('__schedule'), 'subscription');
    }
  };

  // A lone PWYW price's checkout-heading label is defaulted at submit time, not
  // here — only on submit is the final "single price" shape known
  // (prepareSubmitValues in ProductDetailsForm).
  const onPricingChange = next => {
    setFieldValue(field('payWhatYouWant'), next === 'pwyw');
    // A PWYW minimum can be 0, so a pending "Price must be more than 0" block no
    // longer applies once the price becomes pay-what-you-want.
    if (next === 'pwyw' && getNestedValue(errorsRef.current, field('amount'))) {
      setFieldError(field('amount'), undefined);
    }
  };

  const onScheduleChange = next => {
    const p = freshPrice();
    setFieldValue(
      field('planIterations'),
      next === 'payment-plan' ? (p.planIterations > 0 ? p.planIterations : 4) : '',
    );
    if (next === 'payment-plan') {
      setFieldValue(field('payWhatYouWant'), false);
      setFieldValue(field('__pricing'), 'fixed');
    }
  };

  // A yearly anchor needs a month (the renewal pins to a day of a month), so any
  // path that lands on "yearly + anchor enabled" seeds January rather than leaving
  // the month empty. Leaving yearly clears it so a stale month never rides along
  // on a monthly price. The API rejects a month-less yearly anchor as the backstop
  // (validatePriceFields), and the month Select renders a disabled "Select month"
  // placeholder for '' so a missing month can never masquerade as January.
  const syncAnchorMonth = nextInterval => {
    const p = freshPrice();
    if (!p.billingCycleAnchorConfig?.enabled) return;
    if (nextInterval === 'year') {
      if (!p.billingCycleAnchorConfig?.month) {
        setFieldValue(field('billingCycleAnchorConfig.month'), '1');
      }
    } else if (p.billingCycleAnchorConfig?.month) {
      setFieldValue(field('billingCycleAnchorConfig.month'), '');
    }
  };

  // "Repeats" frequency — map the stored interval + intervalCount to a friendly
  // preset. A cadence with no preset (possible via the seller API, e.g. every
  // 3 days) renders as a read-only "Custom" entry — the custom raw inputs were
  // removed (cab05fb6a), so the value can be kept but not edited here.
  const matchedFrequency = FREQUENCY_OPTIONS.find(
    o =>
      o.interval === (priceValues.interval || 'month') &&
      o.intervalCount === (Number(priceValues.intervalCount) || 1),
  );

  const frequencyValue = matchedFrequency?.value ?? 'custom';

  const onFrequencyChange = value => {
    const preset = FREQUENCY_OPTIONS.find(o => o.value === value);
    if (preset) {
      setFieldValue(field('interval'), preset.interval);
      setFieldValue(field('intervalCount'), preset.intervalCount);
      syncAnchorMonth(preset.interval);
    }
  };

  const setTrial = value => {
    const p = freshPrice();
    setFieldValue(field('__trial'), value);
    if (value === 'none') {
      setFieldValue(field('trialPeriodDays'), '');
    } else if (value === 'free') {
      setFieldValue(field('trialPeriodDays'), p.trialPeriodDays > 0 ? p.trialPeriodDays : 14);
      setFieldValue(field('setupFee'), ''); // free trial has no fee
    } else if (value === 'paid') {
      setFieldValue(field('trialPeriodDays'), p.trialPeriodDays > 0 ? p.trialPeriodDays : 14);
      // leave setupFee for the user to enter in the inline field
    }
  };

  // Identity + billing fields rendered through the config-driven form engine so they
  // reuse the standard Label/RadioCard/error styles and the shared visibility +
  // onChange machinery. The remaining bespoke sections stay as JSX below.
  //
  // Per the "Reorder settings in edit price" ticket, only Label leads the form; the
  // Price settings group (billing radios + amount + recurring + tick-to-reveal rows)
  // follows, then Description + Badge after a divider. So Description/Badge are split
  // into `identityAfterPrice` and appended after the price block in `allFields`.
  // Single-price products normally hide the Label (there's no sibling price to
  // distinguish from). The exception is a single PWYW price: its label is the
  // checkout heading above the amount input (replacing the old theme-level
  // "Pay what you want" text), so surface + enable it there.
  const showLabelForSinglePwyw = singlePrice && payWhatYouWant;

  const labelField = !hideIdentity &&
    !hideLabel &&
    (!singlePrice || showLabelForSinglePwyw) && {
      name: field('label'),
      type: 'text',
      label: 'Label',
      placeholder: 'Optional name shown to customers',
      clearable: true,
      // Label/Description only make sense when buyers choose between prices —
      // except a single PWYW price, whose label is its checkout heading.
      disabled: !showLabelForSinglePwyw && enabledPriceCount <= 1,
    };

  const configFields = [
    // A single PWYW price's label heads the amount box, so it sits with the
    // amounts below rather than leading the form.
    !showLabelForSinglePwyw && labelField,
    !lockBilling &&
      allowRecurring && {
        name: field('billingType'),
        type: 'radioCard',
        label: 'Billing',
        columns: 2,
        options: [
          { value: 'one_time', label: 'One-time payment', disabled: billingTypeLocked },
          { value: 'recurring', label: 'Recurring billing', disabled: billingTypeLocked },
        ],
        onChange: onBillingChange,
        helpText: billingTypeLocked
          ? 'Billing type cannot be changed after a purchase has been made.'
          : undefined,
      },
    !lockBilling &&
      allowPaymentPlan && {
        name: field('__schedule'),
        type: 'radioCard',
        label: 'Recurring schedule',
        columns: 1,
        visible: formValues => pv(formValues).billingType === 'recurring',
        options: [
          {
            value: 'subscription',
            label: 'Subscription',
            labelDescription: 'Ongoing until cancelled.',
          },
          {
            value: 'payment-plan',
            label: 'Payment plan',
            labelDescription: 'A fixed number of payments.',
          },
        ],
        onChange: onScheduleChange,
      },
    !lockBilling &&
      !hideAmount &&
      allowPayWhatYouWant && {
        name: field('__pricing'),
        type: 'radioCard',
        label: 'Pricing',
        columns: 1,
        visible: formValues => {
          const x = pv(formValues);
          const bt = x.billingType || 'one_time';
          if (bt === 'one_time') return true;
          if (!allowRecurringPayWhatYouWant) return false;
          const sched = x.__schedule || (x.planIterations > 0 ? 'payment-plan' : 'subscription');
          return bt === 'recurring' && sched === 'subscription';
        },
        options: [
          { value: 'fixed', label: 'Fixed price', labelDescription: 'You set the amount.' },
          {
            value: 'pwyw',
            label: 'Pay what you want',
            labelDescription: 'Customer chooses the amount.',
          },
        ],
        onChange: onPricingChange,
      },
  ].filter(Boolean);

  // Description + Badge — rendered after the Price settings group (see `allFields`),
  // separated by a divider. Same visibility rules as before the reorder.
  const identityAfterPrice = [
    !hideIdentity &&
      !singlePrice && {
        name: field('description'),
        type: 'textarea',
        label: 'Description',
        placeholder: 'Optional description shown to customers',
        autosize: true,
        minRows: 1,
        clearable: true,
        disabled: enabledPriceCount <= 1,
      },
    !hideIdentity &&
      !hideBadge && {
        name: field('badge'),
        type: 'text',
        label: 'Highlight',
        placeholder: 'e.g. Most popular, 20% OFF',
        clearable: true,
        tooltip: 'A badge displayed above the price to highlight this price option.',
      },
  ].filter(Boolean);

  // Recurring-flow fields, also config-driven. Required fields carry
  // `required: true` for validation only — there is no visible marker (the team
  // dropped asterisks; #1165 is closed). Fields with a unit suffix ("Days",
  // currency) are `custom` so the bespoke input keeps its suffix while FieldInner
  // still owns the label.
  const recurringFields = [
    {
      name: field('planIterations'),
      type: 'number',
      label: 'Number of payments',
      required: true,
      placeholder: '4',
      visible: formValues => {
        const x = pv(formValues);
        const sched = x.__schedule || (x.planIterations > 0 ? 'payment-plan' : 'subscription');
        return x.billingType === 'recurring' && sched === 'payment-plan';
      },
    },
    {
      name: field('__trial'),
      type: 'select',
      label: 'Trial',
      visible: formValues => pv(formValues).billingType === 'recurring',
      // A paid trial IS a setup fee, so it goes with the rest of them.
      options: [
        { value: 'none', label: 'None' },
        { value: 'free', label: 'Free' },
        ...(allowSetupFee ? [{ value: 'paid', label: 'Paid' }] : []),
      ],
      onChange: e => setTrial(e.target.value),
    },
    {
      name: field('trialPeriodDays'),
      type: 'custom',
      label: 'Trial duration',
      required: true,
      visible: formValues => {
        const x = pv(formValues);
        return x.billingType === 'recurring' && trialOf(x) !== 'none';
      },
      renderField: () => (
        <div className="flex items-center gap-2 w-40">
          <TextInput
            name={field('trialPeriodDays')}
            id={field('trialPeriodDays')}
            type="number"
            placeholder="14"
          />
          <span className="text-sm text-gray-500 dark:text-muted-foreground shrink-0">Days</span>
        </div>
      ),
    },
    allowSetupFee && {
      name: field('setupFee'),
      type: 'custom',
      label: 'Setup fee',
      required: true,
      visible: formValues => {
        const x = pv(formValues);
        return x.billingType === 'recurring' && trialOf(x) === 'paid';
      },
      renderField: () => (
        <div className="flex items-center gap-2 w-40">
          <TextInput
            name={field('setupFee')}
            id={field('setupFee')}
            type="number"
            step="any"
            placeholder="19.99"
          />
          <span className="text-sm text-gray-500 dark:text-muted-foreground shrink-0">
            {currencyCode}
          </span>
        </div>
      ),
    },
  ];

  // (5) Tick-to-reveal rows — config-driven: a checkbox toggle + a `visible`-gated
  // revealed control. Discounted/Start/Setup track UI-only `__open` flags; Billing
  // anchor + Use Stripe price bind to their real boolean fields. These flow directly
  // under the price/recurring fields (the divider now sits after them, before the
  // Description/Badge identity group — see `allFields`).
  const expandableFields = [
    {
      name: field('__discountOpen'),
      type: 'checkbox',
      label: 'Discounted from',
      tooltip: 'Show strike-through pricing to indicate a discount',
      // Available in no-base-price mode too (hideAmount): a stored
      // discountedFromPrice deliberately renders the $0 price line with the
      // strike-through on the storefront (shouldShowPrice shows a discounted 0).
      visible: fv => !pv(fv).payWhatYouWant,
      onChange: checked => {
        if (!checked) setFieldValue(field('discountedFromPrice'), '');
      },
    },
    {
      name: field('discountedFromPrice'),
      type: 'custom',
      inset: true,
      visible: fv => !pv(fv).payWhatYouWant && !!pv(fv).__discountOpen,
      renderField: () => (
        <div className="w-32 flex items-center gap-2">
          <TextInput
            name={field('discountedFromPrice')}
            type="number"
            step="any"
            placeholder="29.99"
          />
          <span className="text-sm text-gray-500 dark:text-muted-foreground shrink-0">
            {currencyCode}
          </span>
        </div>
      ),
    },
    {
      name: field('__startDateOpen'),
      type: 'checkbox',
      label: 'Start date',
      tooltip:
        'Delay the first charge until a specific date. The date and time are in UTC. To start on the renewal date without a prorated charge, use 00:00.',
      visible: fv => pv(fv).billingType === 'recurring',
      onChange: checked => {
        if (!checked) setFieldValue(field('startDate'), '');
      },
    },
    {
      name: field('startDate'),
      type: 'custom',
      inset: true,
      visible: fv => pv(fv).billingType === 'recurring' && !!pv(fv).__startDateOpen,
      renderField: () => (
        <div className="flex items-center gap-2">
          <div className="w-56">
            <TextInput
              name={field('startDate')}
              type="datetime-local"
              aria-label="Start date (UTC)"
            />
          </div>
          <span className="text-sm text-gray-500 dark:text-muted-foreground shrink-0">UTC</span>
        </div>
      ),
    },
    {
      name: field('billingCycleAnchorConfig.enabled'),
      type: 'checkbox',
      label: 'Renewal date',
      tooltip:
        'Set a fixed renewal day for subscriptions (00:00–23:59 UTC). Monthly and yearly subscriptions started on this day are charged the full price. All other subscriptions and payment plans are prorated for their first billing period.',
      visible: fv => pv(fv).billingType === 'recurring',
      onChange: checked => {
        // freshPrice, not priceValues: the engine's memo keeps this checkbox
        // (and this closure) un-rerendered across interval changes, so the
        // captured priceValues may predate a switch to yearly.
        const p = freshPrice();
        if (checked) {
          if (!p.billingCycleAnchorConfig?.dayOfMonth) {
            setFieldValue(field('billingCycleAnchorConfig.dayOfMonth'), 1);
          }
          // A yearly anchor always carries a month — seed it on enable.
          if (p.interval === 'year' && !p.billingCycleAnchorConfig?.month) {
            setFieldValue(field('billingCycleAnchorConfig.month'), '1');
          }
        } else {
          setFieldValue(field('billingCycleAnchorConfig.dayOfMonth'), 1);
          setFieldValue(field('billingCycleAnchorConfig.month'), '');
        }
      },
    },
    {
      name: field('billingCycleAnchorConfig.dayOfMonth'),
      type: 'custom',
      inset: true,
      visible: fv =>
        pv(fv).billingType === 'recurring' && !!pv(fv).billingCycleAnchorConfig?.enabled,
      renderField: () => (
        <div className="space-y-2">
          <div className="w-40">
            <Label>Day of month</Label>
            <SelectField
              name={field('billingCycleAnchorConfig.dayOfMonth')}
              id={field('billingCycleAnchorConfig.dayOfMonth')}
              transformValue={v => Number(v)}
            >
              {daysOfMonth.map(day => (
                <option value={day} key={day}>
                  {day}
                </option>
              ))}
            </SelectField>
          </div>
          {priceValues.interval === 'year' && (
            <div className="w-40">
              <Label>Month</Label>
              <SelectField
                name={field('billingCycleAnchorConfig.month')}
                id={field('billingCycleAnchorConfig.month')}
              >
                {/* An empty month must LOOK empty: without this option a ''
                    value silently displays the first real option (January)
                    while the form still submits '' — the ghost behind
                    "I accepted the default of Jan" rejections. */}
                <option value="" disabled>
                  Select month
                </option>
                {months.map(m => (
                  <option value={m.value} key={m.value}>
                    {m.label}
                  </option>
                ))}
              </SelectField>
            </div>
          )}
        </div>
      ),
    },
    allowSetupFee && {
      name: field('__setupFeeOpen'),
      type: 'checkbox',
      label: 'Setup fee',
      tooltip: 'One-time fee charged immediately at checkout',
      visible: fv => trialOf(pv(fv)) !== 'paid',
      onChange: checked => {
        if (!checked) setFieldValue(field('setupFee'), '');
      },
    },
    allowSetupFee && {
      name: field('setupFee'),
      type: 'custom',
      inset: true,
      visible: fv => trialOf(pv(fv)) !== 'paid' && !!pv(fv).__setupFeeOpen,
      renderField: () => (
        <div className="space-y-2">
          <div className="w-32 flex items-center gap-2">
            <TextInput name={field('setupFee')} type="number" step="any" placeholder="19.99" />
            <span className="text-sm text-gray-500 dark:text-muted-foreground shrink-0">
              {currencyCode}
            </span>
          </div>
          <Checkbox.Wrapper>
            <Checkbox
              name={field('setupFeeMultipliesWithQuantity')}
              id={field('setupFeeMultipliesWithQuantity')}
            />
            <Checkbox.Label htmlFor={field('setupFeeMultipliesWithQuantity')}>
              Multiply by quantity
            </Checkbox.Label>
          </Checkbox.Wrapper>
        </div>
      ),
    },
    {
      name: field('managedInStripe'),
      type: 'checkbox',
      label: 'Use Stripe price',
      tooltip:
        'Use an existing recurring price from your Stripe account. Amount, currency and frequency come from Stripe; this price is not managed here.',
      // hideAmount: a linked Stripe price carries its own amount — it would
      // reintroduce the base price the variants mode hides.
      visible: fv => !hideAmount && pv(fv).billingType === 'recurring' && !pv(fv).payWhatYouWant,
      onChange: checked => {
        if (!checked) setFieldValue(field('stripePriceId'), '');
      },
    },
    {
      name: field('stripePriceId'),
      type: 'custom',
      inset: true,
      visible: fv =>
        !hideAmount &&
        pv(fv).billingType === 'recurring' &&
        !pv(fv).payWhatYouWant &&
        !!pv(fv).managedInStripe,
      renderField: () => (
        <PricePlanSelector fieldPrefix={fieldPrefix} planId={priceValues.stripePriceId} />
      ),
    },
  ];

  // Bottom "Additional settings" disclosure (saved prices only) — config-driven.
  // Hidden for a lone single price: hide/reference only matter for the multi-price
  // picker, so a single-price product keeps the form minimal.
  const additionalSettingsFields = [
    lockBilling &&
      !singlePrice && {
        type: 'divider',
      },
    lockBilling &&
      !singlePrice && {
        type: 'disclosure',
        label: 'Additional settings',
        fields: [
          {
            name: field('hidden'),
            type: 'checkbox',
            label: 'Hide',
            // Distinct from Disable (`enabled`): a hidden price is removed from the
            // checkout picker but can be revealed by listing the price reference
            // below in the `show_prices` query param.
            tooltip: (
              <>
                Hide this price and show or fill it with query parameters.{' '}
                <Link to="/docs/articles/preselect-checkout-prices" target="_blank" variant="light">
                  Learn more
                </Link>
              </>
            ),
            reduceMargin: true,
          },
          {
            name: field('reference'),
            type: 'text',
            label: 'Reference',
            placeholder: 'prc_…',
            className: 'font-mono text-[13px]',
            helpText:
              'Internal id used in exports and reports. Auto-generated when you add a price.',
          },
        ],
      },
  ].filter(Boolean);

  const splitCurrencyRepeats = payWhatYouWant && isRecurring;
  const currencySuffix = currencyAsSuffix
    ? (priceValues.currency || 'usd').toUpperCase()
    : undefined;

  const currencyField = currencyAsSuffix ? null : (
    <div className="flex-1">
      <Label htmlFor={field('currency')} tooltip={currencyTooltip}>
        Currency
      </Label>
      <Select
        name={field('currency')}
        id={field('currency')}
        aria-label="Currency"
        value={priceValues.currency || 'usd'}
        onChange={e => onCurrencyChange(e.target.value)}
        disabled={managedInStripe || currencyLocked}
        options={currencyOptions}
      />
    </div>
  );
  const repeatsField = isRecurring && (
    <div className="flex-1">
      <Label htmlFor={field('frequency')}>Repeats</Label>
      <Select
        id={field('frequency')}
        aria-label="Repeats"
        value={frequencyValue}
        onChange={e => onFrequencyChange(e.target.value)}
        disabled={managedInStripe}
      >
        {FREQUENCY_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        {!matchedFrequency && (
          <option value="custom" disabled>
            Custom
          </option>
        )}
      </Select>
    </div>
  );

  // (3) Amount / currency / Repeats (+ custom interval) — one `custom` schema entry.
  // The PWYW dual and frequency presets don't map cleanly onto a
  // fixed-grid `columns`, but as a schema entry it still flows through the engine so
  // its outer spacing matches the rest of the form.
  const priceBlock = {
    type: 'custom',
    // This is a *nameless* custom field, so the form engine's memo (Form.js
    // `fieldPropsAreEqual`) can only tell it apart by `id` — it can't see the closure
    // values `renderField` branches on. Encode every structural determinant (index/
    // prefix, currency lock, pricing type, billing cadence) so toggling any of them
    // busts the memo and the React key; otherwise the block stays on a stale render —
    // e.g. bound to the old index, a wrongly-disabled currency selector, or no
    // Suggested price field after switching to pay-what-you-want.
    id: `price-amount-block:${prefix}:${currencyAsSuffix ? 'suffix' : currencyLocked ? 'locked' : 'unlocked'}:${
      payWhatYouWant ? 'pwyw' : 'fixed'
    }:${isRecurring ? 'recurring' : 'onetime'}:${hideAmount ? 'noamount' : 'amount'}`,
    renderField: () => (
      <div className="space-y-2">
        <div className="flex gap-3">
          {hideAmount ? null : !payWhatYouWant ? (
            <div className="flex-1">
              <Label
                htmlFor={field('amount')}
                tooltip={currencyAsSuffix ? currencyTooltip : undefined}
              >
                Price
              </Label>
              <div className="relative">
                <TextInput
                  name={field('amount')}
                  id={field('amount')}
                  type="number"
                  step="any"
                  placeholder="19.99"
                  suffix={currencySuffix}
                  disabled={managedInStripe}
                  onChange={clearAmountErrorIfValid}
                />
              </div>
              <FormError name={field('amount')} />
              <AmountBlockedError index={index} isNew={isNew} />
            </div>
          ) : (
            <>
              <div className="flex-1">
                <Label htmlFor={field('amount')}>Minimum price</Label>
                <div className="relative">
                  <TextInput
                    name={field('amount')}
                    id={field('amount')}
                    type="number"
                    step="any"
                    placeholder="0.00"
                    suffix={currencySuffix}
                  />
                </div>
                <FormError name={field('amount')} />
              </div>
              <div className="flex-1">
                <Label htmlFor={field('pwywSuggestedPrice')}>Suggested price</Label>
                <div className="relative">
                  <TextInput
                    name={field('pwywSuggestedPrice')}
                    id={field('pwywSuggestedPrice')}
                    type="number"
                    step="any"
                    placeholder="19.99"
                    suffix={currencySuffix}
                  />
                </div>
              </div>
            </>
          )}
          {/* Fixed prices and one-time PWYW keep currency + repeats on the amount
              row. A PWYW subscription's min/suggested pair already fills the row,
              so its currency + repeats drop to the second row below. */}
          {!splitCurrencyRepeats && currencyField}
          {!splitCurrencyRepeats && repeatsField}
          {/* The currency select used to take the other half of this row. With
              the code inside the amount instead, a lone field would stretch the
              full width, so an empty half keeps the proportions. */}
          {currencyAsSuffix && !splitCurrencyRepeats && !repeatsField && !payWhatYouWant && (
            <div className="flex-1" aria-hidden="true" />
          )}
        </div>

        {splitCurrencyRepeats && (currencyField || repeatsField) && (
          <div className="flex gap-3">
            {currencyField}
            {repeatsField}
          </div>
        )}

        {isRecurring && (
          <>
            <FormError name={field('interval')} />
            <FormError name={field('intervalCount')} />
          </>
        )}
      </div>
    ),
  };

  // One consolidated schema, rendered in a single pass with `nextFieldType` so the
  // engine's inter-field spacing is consistent across the whole form (mirroring how
  // <Form> renders). PriceForm shares the parent product Formik, so it can't use the
  // <Form> component directly.
  // Render order (see the "Reorder settings in edit price" ticket): Label + billing
  // radios, the amount block, recurring fields and the tick-to-reveal rows (the whole
  // "Price settings" group), then a divider, then Description + Badge, then the
  // "Additional settings" disclosure (which carries its own leading divider). The
  // divider before the identity group is dropped when nothing follows it (e.g. the
  // create-flow draft hides Description/Badge).
  const allFields = [
    ...configFields,
    showLabelForSinglePwyw && labelField,
    priceBlock,
    ...recurringFields,
    ...expandableFields,
    !singlePrice && identityAfterPrice.length > 0 && { type: 'divider' },
    ...identityAfterPrice,
    ...additionalSettingsFields,
  ].filter(Boolean);

  const visibleFields = renderFields({ fields: allFields, values, setFieldValue }).filter(
    f => f && !getIsFieldHidden(f, values),
  );

  return (
    <div className={cn(isPriceDisabled && 'pointer-events-none opacity-50')}>
      {visibleFields.map((f, index) => (
        <Field
          key={f.name || f.id || `field-${index}`}
          field={{ ...f, visible: f.visible === undefined ? true : f.visible }}
          nextFieldType={visibleFields[index + 1]?.type}
          nextFieldInset={visibleFields[index + 1]?.inset}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
        />
      ))}
    </div>
  );
}
